/**
 * Safe Markdown renderer for settled assistant answers (Task 140).
 *
 * Rendered ONLY for completed/errored turns — while a turn is still streaming we
 * render plain text (cheap, smooth, and the surface Task 141's word-reveal
 * animates), then swap to Markdown once `answer.final` settles the text. This is
 * the integration path Task 141 endorses (its option 2) and avoids re-parsing
 * Markdown on every token.
 *
 * Safety (Task 140):
 *  - react-markdown v9 does NOT render raw HTML (no rehype-raw) — HTML in model
 *    output is inert.
 *  - Links are sanitized twice: `urlTransform` drops non-allowed schemes at the
 *    source, and the `a` override re-checks + forces target/rel. javascript:,
 *    data:, etc. become plain text.
 *  - Markdown images have an explicit inert renderer. Model output can mention
 *    an image, but it cannot make the browser request an arbitrary remote URL.
 *  - No GFM/tables/iframes — only the formatting DeepSeek naturally emits
 *    (paragraphs, lists, emphasis, modest headings, inline code, links).
 *  - Source citations/cards live OUTSIDE this component; Markdown never touches
 *    citation metadata.
 */
import { useMemo } from "react";
import Markdown from "react-markdown";
import type { Components } from "react-markdown";
import type { AnswerCitation } from "../api/answer-client";
import { linkifyCitations, parseCiteHref } from "../utils/citation-anchors";

const ALLOWED_SCHEMES = new Set(["http:", "https:", "mailto:"]);

/** Allow relative/anchor links + http(s)/mailto; drop everything else. Exported for the smoke. */
export function sanitizeUrl(url: string): string {
  const trimmed = url.trim();
  if (
    trimmed.startsWith("#") ||
    trimmed.startsWith("/") ||
    trimmed.startsWith("./") ||
    trimmed.startsWith("../")
  ) {
    return trimmed;
  }
  try {
    const parsed = new URL(trimmed);
    return ALLOWED_SCHEMES.has(parsed.protocol) ? trimmed : "";
  } catch {
    return ""; // unparseable -> drop
  }
}

/**
 * Render the answer as safe Markdown. `[n]` markers (Task 144) are rewritten to
 * in-answer anchor links and rendered as citation chips that reveal/scroll to the
 * matching source card; out-of-range markers stay literal text. messageId +
 * sources scope the chips to THIS answer's cards. Both are optional so the
 * transform no-ops (no chips) when there are no sources.
 */
export function AnswerMarkdown(props: {
  text: string;
  messageId?: string;
  sources?: readonly AnswerCitation[];
  /** Citation-chip click: (messageId, 1-based n, the cited source). */
  onCite?: (messageId: string, n: number, citation: AnswerCitation) => void;
}): JSX.Element {
  const messageId = props.messageId ?? "";
  const sources = props.sources ?? [];
  const onCite = props.onCite;

  const components = useMemo<Components>(
    () => ({
      img({ alt }) {
        // Preserve the model's visible label, but deliberately discard src.
        return <span className="answer-md__image-text">{`![${alt ?? ""}]`}</span>;
      },
      a({ href, children }) {
        const safe = typeof href === "string" ? sanitizeUrl(href) : "";
        if (safe === "") {
          // Unsafe/empty href -> render the text, never an active link.
          return <>{children}</>;
        }
        const citeN = parseCiteHref(safe);
        const citation = citeN !== null ? sources[citeN - 1] : undefined;
        if (citeN !== null && messageId !== "" && citation !== undefined) {
          const title = citation.title ?? "";
          return (
            <sup className="answer-cite">
              <button
                type="button"
                className="answer-cite__btn"
                aria-label={title ? `Fuente ${citeN}: ${title}` : `Fuente ${citeN}`}
                {...(title ? { title } : {})}
                onClick={() => onCite?.(messageId, citeN, citation)}
              >
                {citeN}
              </button>
            </sup>
          );
        }
        return (
          <a href={safe} target="_blank" rel="noopener noreferrer" className="answer-md__link">
            {children}
          </a>
        );
      },
    }),
    [messageId, sources, onCite],
  );

  const prepared = useMemo(
    () => linkifyCitations(props.text, messageId, sources.length),
    [props.text, messageId, sources.length],
  );

  return (
    <div className="answer-md" data-testid="answer-text">
      <Markdown components={components} urlTransform={sanitizeUrl}>
        {prepared}
      </Markdown>
    </div>
  );
}
